import { useCallback } from 'react';
import type { ClientProfileDto, UserLanguageCode } from '@/app/store/api/restApi';
import {
  setName,
  setLogin,
  setFloor,
  setPhone,
  setEmail,
  setIntercom,
  setUserName,
  setPhotoUrl,
  setProvider,
  setApartement,
  setEntranceway,
  setVerifiedEmail,
  useAppSelector,
  useAppDispatch,
  setDeliveryAddress,
  setAgreementsAccepted,
  setAreManualCoordinates,
  setIsSubscribedToMailings,
  setPreviouslyAcceptedClientDocuments,
} from '@/app/store';
import { resolveProfileOrderEmail } from '../lib';

export type AppliedClientProfileResult = {
  userLanguage: UserLanguageCode;
};

export const useApplyClientProfile = () => {
  const dispatch = useAppDispatch();
  const { name: orderName, phone, email: orderEmail, delivery } = useAppSelector((state) => state.order);

  return useCallback(
    (profile: ClientProfileDto): AppliedClientProfileResult => {
      const identity = profile.authIdentities[0];

      dispatch(setUserName(profile.name ?? undefined));
      if (orderName === '' && profile.name) dispatch(setName(profile.name));
      if (phone === '' && profile.phone) dispatch(setPhone(profile.phone));
      if (orderEmail === '') {
        const profileEmail = resolveProfileOrderEmail(profile);
        if (profileEmail) dispatch(setEmail(profileEmail));
      }
      dispatch(setVerifiedEmail(profile.verifiedEmail ?? undefined));
      dispatch(setLogin(identity?.login ?? profile.verifiedEmail ?? undefined));
      dispatch(setPhotoUrl(identity?.providerPhotoUrl ?? undefined));
      dispatch(setProvider(identity?.provider ?? undefined));
      dispatch(setAgreementsAccepted(profile.innerUserInfo.acceptedClientDocuments));
      dispatch(setIsSubscribedToMailings(profile.innerUserInfo.isSubscribedToMailings));
      dispatch(setPreviouslyAcceptedClientDocuments(profile.innerUserInfo.hasPreviouslyAcceptedClientDocuments));

      if (!delivery.address && profile.deliveryAddress) {
        const pd = profile.deliveryAddress;
        dispatch(
          setDeliveryAddress({
            address: pd.address,
            coordinates: { lat: pd.point.lat, lng: pd.point.lng },
            city: pd.city ?? '',
            street: pd.street ?? '',
            house: pd.house ?? '',
          }),
        );
        dispatch(setAreManualCoordinates(pd.areManualCoordinates));
        if (pd.floor) dispatch(setFloor(pd.floor));
        if (pd.entranceway) dispatch(setEntranceway(pd.entranceway));
        if (pd.apartment) dispatch(setApartement(pd.apartment));
        if (pd.intercom) dispatch(setIntercom(pd.intercom));
      }

      return { userLanguage: profile.languageCode };
    },
    [delivery.address, dispatch, orderEmail, orderName, phone],
  );
};
